import {AfterViewInit, Component, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '../../state/app.state';
import { dashboardInfo } from '../../state/dashboard/dashboard.actions';
import { selectDashboardInfoSuccess, selectDashboardInfoFailure } from '../../state/dashboard/dashboard.selectors';
import {filter, Subject, take} from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as ApexCharts from 'apexcharts';
import {BillingService} from "../../services/billing.service";
import {AuthService} from "../../services/auth.service";
import { BarcodeFormat } from '@zxing/library';
import { PopupService } from 'src/app/services/popup.service';
import { ZXingScannerComponent } from '@zxing/ngx-scanner';
import {HeaderService} from "../../services/header.service";
import {selectNewSearchFailure, selectNewSearchSuccess} from "../../state/search/search.selectors";
import {newSearch} from "../../state/search/search.actions";
import {EventTypes} from "../../event-types";
import {SharedService} from "../../services/shared.service";
import { AppTourService } from '../../services/app-tour.service';
import {selectTourActive} from "../../state/tour/tour.selectors";
import * as IcpActions from '../../state/icp/icp.actions';
import * as IcpSelectors from '../../state/icp/icp.selectors';
import { ICP } from '../../state/icp/icp.state';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private chartsInitialized = false;

  user: any = { has_completed_setup: undefined };

  dashboard: any = null;
  dataReady = false;

  profileChart: ApexCharts | null = null;
  salesStageChart: ApexCharts | null = null;
  segmentationChart: ApexCharts | null = null;

  activeReports: number = 0;
  totalReports: number = 0;
  profileCompletion: number = null;

  isModalVisible: boolean = false;

  isSearchProspectVisible: boolean = false;
  isRequestAssistanceVisible: boolean = false;
  allowedFormats = [ BarcodeFormat.QR_CODE, BarcodeFormat.EAN_13, BarcodeFormat.CODE_128, BarcodeFormat.DATA_MATRIX /*, ...*/ ];
  isMobile: any;
  hasCameraAccess = false;  // Track camera access status
  scannedCode: string | null = null;

  // Search person overlay control
  showOverlay = false;

  linkedin: string;
  newSearchSuccessSubscription: any;
  newSearchFailureSubscription: any;

  showScanner = false;

  @ViewChild(ZXingScannerComponent) scanner: ZXingScannerComponent | undefined;

  isBulkUploadVisible: boolean = false

  public profileCompletionColor: string = '#4CAF50'; // Default green

  isTourActive: boolean = true;

  tourLoading: boolean = true;

  dataLoading = true;

  // ICP-related properties
  userIcps: ICP[] = [];
  defaultIcp: ICP | null = null;
  hasIcps: boolean = false;
  icpsLoading: boolean = true;

  // Helper to get the ICP to edit (default ICP or first ICP)
  get icpToEdit(): ICP | null {
    return this.defaultIcp || (this.userIcps.length > 0 ? this.userIcps[0] : null);
  }

  constructor(
    private authService : AuthService,
    private store: Store<AppState>,
    private billingService: BillingService,
    private popupService: PopupService,
    private headerService: HeaderService,
    private sharedService: SharedService,
    private appTourService: AppTourService
  ) {}

  ngOnInit(): void {
    // Set initial loading state
    this.tourLoading = true;

    this.headerService.setHeaderState('Dashboard', null, true);

    // Get dashboard info and other initializations
    this.getDashboardInfo();

    // Check tour state from store
    this.store.select(selectTourActive)
      .pipe(takeUntil(this.destroy$))
      .subscribe(active => {
        this.isTourActive = active;

        // Tour is now active, we can remove loading state
        if (active) {
          this.tourLoading = false;
        }
      });

    // Get user data FIRST, then load ICPs
    this.authService.authUser()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (user) => {
          if (user) {
            this.user = user;
            // Note: We no longer rely on has_completed_setup for dashboard logic
            // Instead, we check actual ICP data (hasIcps flag)
            // This localStorage is kept for backwards compatibility with other components
            localStorage.setItem('user_setup_completed', user.has_completed_setup === 1 ? 'true' : 'false');

            // Now that user is loaded, load their ICPs
            this.loadUserIcps();
          }
        },
        error: (err) => {
          console.error('Error fetching user data:', err);
          this.icpsLoading = false; // Stop ICP loading if user fetch fails
        }
      });
  }

  ngAfterViewInit(): void {
    // Wait for ICPs to load before initializing tour
    // This ensures we have the data needed to decide whether to show tour
    this.waitForIcpsAndInitializeTour();
  }

  // Wait for ICPs to load, then initialize tour
  private waitForIcpsAndInitializeTour(): void {
    // Subscribe to ICP loading state
    this.store.select(IcpSelectors.selectIcpsLoading)
      .pipe(
        filter(loading => !loading), // Wait until loading is false
        take(1), // Take only first emission
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Now that ICPs are loaded, we can safely check and initialize tour
        this.initializeTour();
      });
  }

  // Load user's ICPs
  private loadUserIcps(): void {
    // Don't load ICPs if user is not authenticated
    if (!this.user || !this.user.id) {
      this.icpsLoading = false;
      return;
    }

    this.icpsLoading = true;

    // Dispatch action to load all ICPs
    this.store.dispatch(IcpActions.loadIcps({
      page: 1,
      perPage: 100 // Load all ICPs
    }));

    // Subscribe to ICP list
    this.store.select(IcpSelectors.selectIcps)
      .pipe(takeUntil(this.destroy$))
      .subscribe(icps => {
        this.userIcps = icps || [];
        this.hasIcps = this.userIcps.length > 0;
        this.icpsLoading = false;

        // Update localStorage based on actual ICP data
        if (this.hasIcps) {
          localStorage.setItem('user_setup_completed', 'true');
        }
      });

    // Subscribe to default ICP
    this.store.select(IcpSelectors.selectDefaultIcp)
      .pipe(takeUntil(this.destroy$))
      .subscribe(defaultIcp => {
        this.defaultIcp = defaultIcp;
      });
  }

  // New method - single path for tour initialization
  // In your dashboard.component.ts - modify the initializeTour method

  private initializeTour(): void {
    // CRITICAL CHECK: Check directly if dashboard tour is completed - FIRST THING
    const dashboardTourCompleted = localStorage.getItem('salesommo_tour_completed_dashboard') === 'true';

    if (dashboardTourCompleted) {
      this.tourLoading = false;
      return; // EXIT EARLY - this is crucial
    }

    // NEW CHECK: If user has ICPs, don't show the tour
    if (this.hasIcps) {
      this.tourLoading = false;
      // Mark tour as completed since user has ICPs
      localStorage.setItem('salesommo_tour_completed_dashboard', 'true');
      localStorage.setItem('user_setup_completed', 'true');
      return;
    }

    // Check if the ICP section element exists
    const icpSection = document.querySelector('#icp-optimization-section');
    if (!icpSection) {
      console.error('Cannot initialize tour - #icp-optimization-section element not found');
      this.tourLoading = false;
      return;
    }

    // Second check: user setup
    const userSetupCompleted = localStorage.getItem('user_setup_completed') === 'true';
    if (userSetupCompleted) {
      this.tourLoading = false;
      return;
    }

    // If we get here, show the tour (user has no ICPs)

    // IMPORTANT: Call startTour directly, NOT checkAndShowTourForCurrentRoute
    setTimeout(() => {
      this.appTourService.startTour();
      this.tourLoading = false;
    }, 300);
  }

  toggleScanner() {
    this.showScanner = !this.showScanner;
  }
  updateProfileCompletionColor(completionValue: number): string {
    if (completionValue < 50) {
      return '#FF3A2D'; // Red for scores 0-49
    } else if (completionValue < 100) {
      return '#FC8226'; // Orange for scores 50-99
    } else {
      return '#4CAF50'; // Green for score 100
    }
  }

  getDashboardInfo() {
    // Set data loading state
    this.dataLoading = true;

    // Dispatch the action to fetch dashboard info
    this.store.dispatch(dashboardInfo());

    // Listen for success response
    this.store
      .select(selectDashboardInfoSuccess)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          if (result && result.dashboard) {
            this.dashboard = result.dashboard;

            this.activeReports = result.dashboard.total_searches - result.dashboard.total_searches_archived;
            this.totalReports = result.dashboard.total_searches;
            this.profileCompletion = result.dashboard.profile_completion;

            // Set the color only after we get the profile completion value
            this.profileCompletionColor = this.updateProfileCompletionColor(this.profileCompletion);

            this.dataReady = true;
            this.dataLoading = false;

            // Initialize charts after ensuring DOM elements are available
            setTimeout(() => this.initCharts(), 0);
          }
        },
        error: (error) => {
          console.error('Error loading dashboard info:', error);
          this.dataLoading = false;
        },
      });

    // Handle failure response
    this.store
      .select(selectDashboardInfoFailure)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (error) => {
          if (error) {
            console.error('Failed to load dashboard info:', error);
            this.dataLoading = false;
            // Optional: Show a notification or take other appropriate actions
          }
        },
      });
  }

  initCharts() {
    // Prevent multiple initializations
    if (this.chartsInitialized || !this.dataReady || !this.dashboard) {
      return;
    }

    // Use setTimeout to delay chart initialization until after the next render cycle
    setTimeout(() => {
      // Clean up any existing charts before initializing
      this.cleanupCharts();

      // Initialize charts
      this.initProfileCompletedChart();
      this.getSalesStageChart();
      this.getSegmentationChart();

      // Mark charts as initialized
      this.chartsInitialized = true;
    }, 50); // Short delay to ensure DOM is ready
  }


  initProfileCompletedChart() {
    const chartContainer = document.querySelector('#profile-completed-chart');
    if (!chartContainer) {
      console.error('Chart container #profile-completed-chart does not exist');
      return;
    }

    const sanitizedValue = Math.max(0, parseFloat(this.dashboard?.profile_completion) || 0);

    // Use the same color that was already set in getDashboardInfo
    // We'll update it here again just to ensure consistency
    this.profileCompletionColor = this.updateProfileCompletionColor(sanitizedValue);

    // Determine the appropriate data attribute for the container
    let completionLevel = 'medium';
    if (sanitizedValue < 50) {
      completionLevel = 'low';
    } else if (sanitizedValue >= 100) {
      completionLevel = 'high';
    }
    chartContainer.setAttribute('data-completion-level', completionLevel);

    const options = {
      series: [sanitizedValue],
      chart: {
        type: 'radialBar',
        height: 175, // Fixed height
        width: 175,  // Fixed width
      },
      plotOptions: {
        radialBar: {
          hollow: {
            size: '60%', // Adjust the size of the hollow center
          },
          track: {
            background: '#E3F3FF',
          },
          dataLabels: {
            name: {
              show: false, // Hide the "name" label
            },
            value: {
              show: true, // Show the value
              offsetY: 10, // Adjust the vertical position
              formatter: (val: number) => `${val}%`, // Format the value
            },
          },
        },
      },
      colors: [this.profileCompletionColor], // Use the dynamically determined color
    };

    // Ensure only one chart instance is active
    if (this.profileChart) {
      this.profileChart.destroy();
    }

    this.profileChart = new ApexCharts(chartContainer, options);
    this.profileChart.render();
  }

  getSalesStageChart() {
    const chartContainer = document.querySelector('#sales-stage-chart');
    const legendContainer = document.querySelector('#sales-stage-legend');

    if (!chartContainer || !legendContainer) {
      console.error('Chart or legend container for sales stage chart does not exist');
      return;
    }

    if (!this.dashboard?.deal_stages_count) {
      console.warn('No data for sales stage chart.');
      return;
    }

    const dealStages = this.dashboard.deal_stages_count;
    const series = [];
    const labels = [];

    for (const [key, value] of Object.entries(dealStages)) {
      try {
        const parsedKey = JSON.parse(key);
        const label = parsedKey.en || key;
        const numericValue = Number(value);

        labels.push(label);
        series.push(isNaN(numericValue) || numericValue < 0 ? 0 : numericValue);
      } catch (error) {
        console.error('Failed to parse key:', key, error);
      }
    }

    const options = {
      series,
      chart: {
        type: 'donut',
        height: 250,
        width: 250,
      },
      labels,
      colors: ['#3B82F6', '#A78BFA', '#67E8F9', '#FACC15', '#34D399', '#FB7185'],
      plotOptions: {
        pie: {
          donut: {
            size: '60%',
          },
        },
      },
      legend: {
        show: false, // Disable the default legend
      },
      dataLabels: {
        enabled: true, // Enable data labels to show values
        formatter: function (val: number, opts: any) {
          return `${opts.w.globals.series[opts.seriesIndex]}`; // Display the raw value
        },
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
          colors: ['#fff'], // Set data label color
        },
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: function (val: number) {
            return `${val}`; // Tooltip format
          },
        },
      },
    };

    this.salesStageChart = new ApexCharts(chartContainer, options);
    this.salesStageChart.render();

    // Custom legend rendering
    legendContainer.innerHTML = labels
      .map(
        (label, index) =>
          `<div class="flex items-center space-x-1 legend-item">
        <span class="flex w-3 h-3 rounded-full" style="background-color: ${options.colors[index]};"></span>
        <span class="text-sm text-secondary">${label}</span>
      </div>`
      )
      .join('');
  }

  getSegmentationChart() {
    const chartContainer = document.querySelector('#segmentation-chart');
    const legendContainer = document.querySelector('#segmentation-legend');

    if (!chartContainer || !legendContainer) {
      console.error('Chart or legend container for segmentation chart does not exist');
      return;
    }

    if (!this.dashboard?.my_judgement_count) {
      console.warn('No data for segmentation chart.');
      return;
    }

    const segmentationData = this.dashboard.my_judgement_count;
    const series = [];
    const labels = [];

    for (const [key, value] of Object.entries(segmentationData)) {
      try {
        const parsedKey = JSON.parse(key);
        const label = parsedKey.en || key;
        const numericValue = Number(value);

        labels.push(label);
        series.push(isNaN(numericValue) || numericValue < 0 ? 0 : numericValue);
      } catch (error) {
        console.error('Failed to parse key:', key, error);
      }
    }

    const options = {
      series,
      chart: {
        type: 'donut',
        height: 250, // Fixed chart height
        width: 250, // Fixed chart width
      },
      labels,
      colors: ['#FACC15', '#64748B', '#FB7185'],
      plotOptions: {
        pie: {
          donut: {
            size: '60%', // Ensure consistent donut radius
          },
        },
      },
      legend: {
        show: false, // Disable the default legend
      },
      dataLabels: {
        enabled: true, // Enable data labels to show values
        formatter: function (val: number, opts: any) {
          return `${opts.w.globals.series[opts.seriesIndex]}`; // Display the raw value
        },
        style: {
          fontSize: '12px',
          fontWeight: 'bold',
          colors: ['#fff'], // Set data label color
        },
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: function (val: number) {
            return `${val}`; // Tooltip format
          },
        },
      },
    };

    this.segmentationChart = new ApexCharts(chartContainer, options);
    this.segmentationChart.render();

    // Manually generate and render the legend
    legendContainer.innerHTML = labels
      .map(
        (label, index) =>
          `<div class="flex items-center space-x-1 legend-item">
        <span class="flex w-3 h-3 rounded-full" style="background-color: ${options.colors[index]}"></span>
        <span class="text-sm text-secondary">${label}</span>
      </div>`
      )
      .join('');
  }

  ngOnDestroy(): void {
    // Clean up charts and subscriptions
    this.chartsInitialized = false;
    this.destroy$.next();
    this.destroy$.complete();

    this.cleanupCharts();

    document.body.classList.remove('no-scroll');
  }

  cleanupCharts(): void {
    if (this.profileChart) {
      this.profileChart.destroy();
      this.profileChart = null;
    }

    if (this.salesStageChart) {
      this.salesStageChart.destroy();
      this.salesStageChart = null;
    }

    if (this.segmentationChart) {
      this.segmentationChart.destroy();
      this.segmentationChart = null;
    }

    const chartContainers = ['#profile-completed-chart', '#sales-stage-chart', '#segmentation-chart'];
    chartContainers.forEach((selector) => {
      const container = document.querySelector(selector);
      if (container) {
        while (container.firstChild) {
          container.removeChild(container.firstChild);
        }
      }
    });
  }

  openBillingPortal() {
    this.billingService.openBillingPortal();
  }

  closeModal() {
    this.isModalVisible = false;
    document.body.classList.remove('no-scroll');
  }

  preventClose(event: Event) {
    event.stopPropagation();
  }

  openSearchProspect() {
    this.isSearchProspectVisible = true;
  }

  onSearchProspectModalClosed() {
    this.isSearchProspectVisible = false;
  }

  openRequestAssistance() {
    this.isRequestAssistanceVisible = true;
  }

  onRequestAssistanceModalClosed() {
    this.isRequestAssistanceVisible = false;
  }

  onCodeResult(result: string): void {

    const linkedinRegex = /^https:\/\/www\.linkedin\.com\/(?!.*https:\/\/www\.linkedin\.com\/).*$/;

    if (!linkedinRegex.test(result)) {
      this.popupService.error("Error", "The QR code is not a valid linkedin profile.", "Try Again");
      this.showScanner =  false;
    }
    else {
      result = result.replace(/[\?&]fromQR=1(&|$)/, '');


      this.sharedService.emitEvent(EventTypes.SHOW_LOADING_REPORT_BANNER, { message: 'Loading Report Banner' });


      this.store.dispatch(newSearch({ user_id: this.user.id, linkedin_profile: result }));

      this.linkedin = ""

      // Listen for the all searches success or failure from the store
      const newSearchSuccess$ = this.store.select(selectNewSearchSuccess);
      const newSearchFailure$ = this.store.select(selectNewSearchFailure);

      // Subscribe to the all searches success
      this.newSearchSuccessSubscription = newSearchSuccess$.pipe(
        filter(response => response !== null),
        take(1)
      ).subscribe({
        next: (response) => {
          // Handle success

          // this.popupService.notify('Search was done successfully.', 'Search successfully', 'success');

          if (this.newSearchSuccessSubscription) {
            this.newSearchSuccessSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
          }
        },
        error: (error) => {
          // Handle error
          console.error('Login error:', error);

          if (this.newSearchSuccessSubscription) {
            this.newSearchSuccessSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
          }
        }
      });

      // Subscribe to the search failure
      this.newSearchFailureSubscription = newSearchFailure$.pipe(
        filter(failure => failure !== null), // Filter for the failure condition, assuming failure is null when there's no error
        take(1) // Take the first occurrence
      ).subscribe({
        next: (error) => {
          // Handle failure
          const errorMessage = error.error.message
          this.popupService.notify(errorMessage, 'Search error', 'error');

          this.sharedService.emitEvent(EventTypes.CLOSE_REPORT_BANNER, { message: 'Close Report Banner' });

          if (this.newSearchFailureSubscription) {
            this.newSearchFailureSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
          }
        },
        error: (error) => {
          // Handle error in error handling
          console.error('Search error handling error:', error);

          if (this.newSearchFailureSubscription) {
            this.newSearchFailureSubscription.unsubscribe(); // Unsubscribe to avoid memory leaks
          }
        }
      });

      this.toggleScanner();
    }
  }

  toggleCamera() {
    if(!this.hasCameraAccess){
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        navigator.mediaDevices.getUserMedia({ video: true })
          .then(() => {
            this.hasCameraAccess = true;
            this.toggleScanner();
          })
          .catch(error => {
            this.hasCameraAccess = false;

            this.toggleScanner();

            this.popupService.error("Camera access denied", "If you want to use this feature, you need to allow camera permissions.", "Ok");
            console.error(error);
          });
      } else {
        // MediaDevices Api is not supported in this browser
        this.hasCameraAccess = false;
        this.toggleScanner();
      }}else{
      this.toggleScanner();
    }
  }

  openBulkUpload() {
    this.isBulkUploadVisible = true;
  }
  onBulkUploadModalClosed() {
    this.isBulkUploadVisible = false;
  }

  /**
   * Handle search person dropdown visibility changes
   * Used to control overlay when dropdown is open
   * @param isVisible - Whether the dropdown is currently visible
   */
  onDropdownVisibility(isVisible: boolean) {
    this.showOverlay = isVisible;
  }

}
